import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import Loader from "@/components/ui/Loader";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import Table from "components/ui/my-data-table";
import useSWR from "swr";
import { columns } from "./data-table-column";
import MyPagination from "components/ui/MyPagination";
import MyRefetchButton from "components/ui/MyRefetchButton";
import { Button } from "@/components/ui/button";
import Selector from "components/ui/Selector";
import DropdownLang from "components/DropdownLang";
import { X } from "lucide-react";

const PAGE_SIZE = 60;

const TYPE_OPTIONS = [
  { code: "character", name: "character" },
  { code: "bg", name: "bg" },
  { code: "fg", name: "fg" },
  { code: "bgEffect", name: "bgEffect" },
  { code: "fgEffect", name: "fgEffect" },
  { code: "bgStatic", name: "bgStatic" },
  { code: "fgStatic", name: "fgStatic" },
];

export const OnlinesTab = () => {
  const [type, setType] = useState<string | null>(null);
  const [lang, setLang] = useState<number | null>(null);
  const [after, setAfter] = useState<string | null>(null);
  const [afters, setAfters] = useState<string[]>([]);

  const resetPagination = () => {
    setAfter(null);
    setAfters([]);
  };

  const swrParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      ...(lang != null && lang > 0 ? { lang } : {}),
      ...(type ? { type } : {}),
      ...(after ? { after } : {}),
    }),
    [type, lang, after],
  );

  const { data, isLoading, isValidating, mutate } = useSWR(["/spine-frames", swrParams]);

  const nextAfter =
    data?.length >= PAGE_SIZE ? data[data.length - 1]?.id : undefined;

  const goAfter = (next: string | null) => {
    setAfter(next);
    if (next === null) {
      setAfters([]);
    } else {
      const index = afters.indexOf(next);
      if (index === -1) setAfters([...afters, next]);
      else setAfters(afters.slice(0, index + 1));
    }
  };

  const hasType = type !== null && type !== undefined && type !== "";

  const handleTypeChange = (value: string | number | null) => {
    setType(value == null || value === "" ? null : String(value));
    resetPagination();
  };

  const handleLangChange = (value: number | null) => {
    setLang(value != null && Number(value) > 0 ? Number(value) : null);
    resetPagination();
  };

  const table = useReactTable({
    data: data || [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => (row.id ? String(row.id) : String(row.index)),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col h-[calc(100vh-150px)] space-y-4">
      <div className="flex flex-wrap items-center gap-4 shrink-0">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
          className="w-auto m-0"
        />
        <MyRefetchButton onClick={() => mutate()} />
        <div className="flex items-center gap-2">
          <label htmlFor="spine-onlines-type" className="text-sm font-medium">
            Type:
          </label>
          <div className="relative">
            <Selector
              id="spine-onlines-type"
              datas={TYPE_OPTIONS}
              labelName="Type"
              value={hasType ? type : null}
              update={handleTypeChange}
              placeholder="All"
            />
            {hasType ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                title="Clear type"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTypeChange(null);
                }}
                className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="spine-onlines-lang" className="text-sm font-medium">
            Lang:
          </label>
          <DropdownLang value={lang} update={handleLangChange} />
        </div>
      </div>

      {(isLoading || isValidating) && (
        <Loader>加载 Spine 在线列表...</Loader>
      )}
      {!isLoading && !isValidating && (
        <>
          {!data || data.length === 0 ? (
            <Card>
              <CardContent className="p-6">
                <p className="text-muted-foreground">
                  暂无在线 Spine 模版。
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="flex-1 flex flex-col min-h-0">
              <CardContent className="flex-1 min-h-0 overflow-hidden pb-0">
                <div className="h-full overflow-auto">
                  <Table table={table} />
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
